import React, { useEffect, useState } from 'react';
import { BoxesIcon, CloudIcon, CpuIcon, ShieldCheckIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
type Allocation = {
  id: string;
  label: string;
  amount: string;
  percent: number;
  Icon: React.ElementType;
  detail: string;
  connects: string;
  color: string;
};
const allocations: Allocation[] = [{
  id: 'inventory',
  label: 'Inventory & working capital',
  amount: '$1.15M',
  percent: 23,
  Icon: BoxesIcon,
  detail: 'Microdose cartridge stock and JuLi™ build capital ahead of committed orders.',
  connects: 'Funds the consumables annuity',
  color: '#60a5fa'
}, {
  id: 'talent',
  label: 'Talent acquisition & retention',
  amount: '$1.00M',
  percent: 20,
  Icon: UsersIcon,
  detail: 'Field service, deployment, and enterprise account coverage.',
  connects: 'Scales the deployment engine',
  color: '#34d399'
}, {
  id: 'balance-sheet',
  label: 'Financial strength',
  amount: '$0.85M',
  percent: 17,
  Icon: ShieldCheckIcon,
  detail: 'Balance-sheet depth required to sign enterprise and foodservice contracts.',
  connects: 'Unlocks Compass, Sysco scale',
  color: '#fbbf24'
}, {
  id: 'revenue',
  label: 'Revenue growth',
  amount: '$0.80M',
  percent: 16,
  Icon: TrendingUpIcon,
  detail: 'Sales enablement, channel activation, and customer retention programs.',
  connects: 'Drives 1,500 units in 2027',
  color: '#f472b6'
}, {
  id: 'operations',
  label: 'Operations & infrastructure',
  amount: '$0.65M',
  percent: 13,
  Icon: CpuIcon,
  detail: 'Manufacturing capacity, logistics, and service infrastructure.',
  connects: 'Protects gross margin',
  color: '#a78bfa'
}, {
  id: 'product',
  label: 'Product development & launch',
  amount: '$0.55M',
  percent: 11,
  Icon: CloudIcon,
  detail: 'JuLi™ 5.0 rollout and the SIP software platform.',
  connects: 'Builds the intelligence layer',
  color: '#22d3ee'
}];
const outcomes: Array<[string, string]> = [['1,500', 'JuLi™ units deployed in 2027'], ['$27.8M', '2027 revenue, 72% consumables'], ['$4.6M', '2027 EBITDA — cash-flow positive']];
export function UseOfFundsVisual() {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    const clear = () => setActive(null);
    window.addEventListener('beforeprint', clear);
    return () => window.removeEventListener('beforeprint', clear);
  }, []);
  return <section className="grid min-h-0 min-w-0 flex-1 gap-3 lg:grid-cols-[.68fr_1.32fr] lg:gap-5" aria-label="Use of the $5M growth round" data-id="element-1881" data-mp="fed3sqhw6qqh">
      <div className="flex min-h-0 min-w-0 flex-col gap-2.5" data-id="element-1882" data-mp="jfglliji2wuc">
        <div className="relative flex flex-col justify-center overflow-hidden rounded-2xl border-2 border-blue-400 bg-blue-500 p-4 text-white" data-id="element-1883" data-mp="of2uxgw7jsxv">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-50" data-id="element-1884" data-mp="1uuvf2cci36kp">Series C — growth round</p>
          <p className="mt-1 text-4xl font-bold leading-none tabular-nums" data-id="element-1885" data-mp="1zwktm1yxm2dj">$5.0M</p>
          <p className="mt-2 text-sm font-bold leading-snug" data-id="element-1886" data-mp="29xwxhbspfjg4">Every dollar is deployed against the 2027 plan — not general runway.</p>
        </div>

        <div className="flex flex-1 flex-col justify-center rounded-2xl border border-white/15 bg-white/5 p-3.5" data-id="element-1887" data-mp="208dc9zhirdi3">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-300" data-id="element-1888" data-mp="1w7jm0zgltmm1">What this capital buys</p>
          <dl className="mt-2.5 space-y-2" data-id="element-1889" data-mp="1r8ylt01cr2xl">
            {outcomes.map(([value, label]) => <div key={label} className="flex items-baseline gap-2.5 border-b border-white/10 pb-2 last:border-0 last:pb-0" data-id="element-1890" data-mp="1m7st327ozfo2">
                <dt className="shrink-0 text-xl font-bold tabular-nums leading-none text-white" data-id="element-1891" data-mp="1ha9cvwrfosth">{value}</dt>
                <dd className="text-[11px] font-semibold leading-snug text-slate-300" data-id="element-1892" data-mp="1d9yxjthr4q1y">{label}</dd>
              </div>)}
          </dl>
        </div>

        <div className="rounded-2xl border border-blue-300/35 bg-blue-400/10 p-3.5" data-id="element-1893" data-mp="37lx9423j9y10">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-200" data-id="element-1894" data-mp="3eovff16e9cz8">Why this round is the last dilutive one</p>
          <p className="mt-1.5 text-[11px] leading-relaxed text-slate-300" data-id="element-1895" data-mp="3jn5c5g7rolmy">
            The plan reaches positive EBITDA in 2027. This capital funds the working-capital gap between committed orders and the consumables revenue they generate.
          </p>
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-col rounded-2xl border border-white/15 bg-white/5 p-3.5 sm:p-4" data-id="element-1896" data-mp="2vylysm9v1io0">
        <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-1897" data-mp="30qdv7dfp6im2">
          <p className="text-sm font-bold text-white" data-id="element-1898" data-mp="2m1hdeonu9r89">Allocation of proceeds</p>
          <p className="text-[10px] font-semibold text-blue-200" data-id="element-1899" data-mp="2qzj703iw58xz">Select a line to see what it unlocks</p>
        </div>

        <div className="mt-2.5 flex h-3 overflow-hidden rounded-full bg-navy-950/60" aria-hidden="true" data-id="element-1900" data-mp="2czrktfi80m8w">
          {allocations.map((item, index) => <span key={item.id} className={`pd3-uof-segment h-full transition-opacity duration-300 ${active && active !== item.id ? 'opacity-25' : 'opacity-100'}`} style={{
          width: `${item.percent}%`,
          backgroundColor: item.color,
          animationDelay: `${index * 90}ms`
        }} data-id="element-1901" data-mp="2i17axlz99iy5" data-mpi={index} />)}
        </div>

        <ul className="mt-3 flex min-h-0 flex-1 flex-col justify-between gap-1.5" data-id="element-1902" data-mp="23c5sb3niepd4">
          {allocations.map((item, index) => {
          const isActive = active === item.id;
          const dimmed = active !== null && !isActive;
          return <li key={item.id} data-id="element-1903" data-mp="2cb9c69ui38so" data-mpi={index}>
                <button type="button" onClick={() => setActive(isActive ? null : item.id)} aria-pressed={isActive} className={`grid w-full grid-cols-[auto_1fr_auto] items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${isActive ? 'bg-white/10' : 'border-white/10 bg-navy-950/40 hover:border-white/25'} ${dimmed ? 'opacity-40' : 'opacity-100'}`} style={isActive ? {
              borderColor: item.color
            } : undefined} data-id="element-1904" data-mp="2r29l0fz5t4pc">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isActive ? 'text-navy-950' : 'bg-white/10'}`} style={isActive ? {
                backgroundColor: item.color
              } : {
                color: item.color
              }} data-id="element-1905" data-mp="21q261u8ccygw">
                    <item.Icon className="h-4 w-4" aria-hidden="true" data-id="element-1906" data-mp="2geypenfulxwt" />
                  </span>

                  <span className="min-w-0" data-id="element-1907" data-mp="2lel12sg1363i">
                    <span className="flex items-baseline justify-between gap-2" data-id="element-1908" data-mp="2lfmgf942uer1">
                      <span className="truncate text-xs font-bold text-white" data-id="element-1909" data-mp="2pfwhq8gxcgzk">{item.label}</span>
                      <span className="shrink-0 text-[10px] font-bold tabular-nums" style={{
                    color: item.color
                  }} data-id="element-1910" data-mp="r24v7sr59tz6">{item.percent}%</span>
                    </span>
                    <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-white/10" data-id="element-1911" data-mp="njfm3byidf0h">
                      <span className="pd3-val-bar block h-full rounded-full" style={{
                    ['--pd3-val-w' as string]: `${item.percent / 23 * 100}%`,
                    backgroundColor: item.color,
                    animationDelay: `${index * 110}ms`
                  }} data-id="element-1912" data-mp="irnsstnywv6n" />
                    </span>
                    <span className="mt-1 block truncate text-[10px] leading-snug text-slate-400" data-id="element-1913" data-mp="16i0auxcxg8ay">
                      {isActive ? item.detail : item.connects}
                    </span>
                  </span>

                  <span className="shrink-0 text-sm font-bold tabular-nums text-white" data-id="element-1914" data-mp="xg0nlnbbk7zk">{item.amount}</span>
                </button>
              </li>;
        })}
        </ul>

        <p className="mt-2.5 border-t border-white/15 pt-2 text-[9px] leading-relaxed text-slate-400" data-id="element-1915" data-mp="3i7koxj2xztyg">
          Source: Company management. Allocation reflects the planned deployment of the $5.0M Series C round and is subject to change based on commercial timing.
        </p>
      </div>
    </section>;
}